<template>
  <div class="aic-limit" :class="`is-${limit.reason}`" role="status">
    <div class="aic-limit__icon" aria-hidden="true">
      <svg v-if="limit.reason === 'blocked'" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" /></svg>
      <svg v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
    </div>
    <div class="aic-limit__body">
      <strong class="aic-limit__title">{{ title }}</strong>
      <p class="aic-limit__text">{{ text }}</p>
      <p v-if="countdown && countdown !== 'now'" class="aic-limit__timer">
        Available again in <b>{{ countdown }}</b><span v-if="resetClock"> · at {{ resetClock }}</span>
      </p>
      <p class="aic-limit__more">{{ countdown && countdown !== 'now' ? 'Need help before then?' : 'Need help?' }} Our team answers in person.</p>
      <div class="aic-limit__actions">
        <router-link to="/contact" class="aic-limit__btn aic-limit__btn--dark" @click="$emit('navigate')">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
          Contact us
        </router-link>
        <a :href="whatsappHref" target="_blank" rel="noopener" class="aic-limit__btn aic-limit__btn--wa">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12.04 2a9.9 9.9 0 0 0-8.47 15.03L2 22l5.12-1.51A9.9 9.9 0 1 0 12.04 2zm0 18.05a8.1 8.1 0 0 1-4.14-1.14l-.3-.18-3.04.9.92-2.96-.2-.31a8.13 8.13 0 1 1 6.76 3.69zm4.46-6.08c-.24-.12-1.44-.71-1.66-.79-.22-.08-.38-.12-.55.12-.16.24-.63.79-.77.95-.14.16-.28.18-.52.06-.24-.12-1.03-.38-1.96-1.21-.72-.64-1.21-1.44-1.35-1.68-.14-.24-.02-.37.1-.49.11-.11.24-.28.37-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.55-1.32-.75-1.81-.2-.48-.4-.41-.55-.42h-.47c-.16 0-.42.06-.65.3-.22.24-.85.83-.85 2.03s.87 2.36.99 2.52c.12.16 1.72 2.62 4.16 3.67.58.25 1.03.4 1.39.51.58.19 1.11.16 1.53.1.47-.07 1.44-.59 1.64-1.16.2-.57.2-1.05.14-1.16-.06-.1-.22-.16-.46-.28z" /></svg>
          WhatsApp
        </a>
        <button v-if="limit.reason === 'conversation'" type="button" class="aic-limit__btn aic-limit__btn--ghost" @click="$emit('new')">Start a new conversation</button>
        <router-link v-if="showSignIn" to="/login" class="aic-limit__btn aic-limit__btn--ghost" @click="$emit('navigate')">Sign in for more</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import { whatsappLink } from '@/data/company';

const props = defineProps({
  limit: { type: Object, required: true },
  countdown: { type: String, default: '' },
  signedIn: { type: Boolean, default: false },
});
defineEmits(['navigate', 'new']);

const TITLES = {
  messages: 'You have reached your message limit',
  photos: 'You have reached your photo limit',
  renders: 'You have used your room pictures for today',
  conversation: 'This conversation is getting long',
  blocked: 'The assistant is not available here',
  daily_budget: 'The assistant is resting for today',
  monthly_budget: 'The assistant is resting this month',
  burst: 'One moment…',
};
const title = computed(() => TITLES[props.limit.reason] || 'The assistant is paused');
const text = computed(() => {
  const q = props.limit.quota;
  switch (props.limit.reason) {
    case 'messages':
      return `Each ${props.signedIn ? 'account' : 'device'} can send ${q?.limit_messages || 'a number of'} messages per 24 hours, and you have used them all. The counter frees up on its own — no need to do anything.`;
    case 'photos':
      return `You have sent the ${q?.limit_photos || 'maximum'} photos allowed per 24 hours. You can keep asking questions without a photo.`;
    case 'renders':
      return `Each ${props.signedIn ? 'account' : 'device'} can create ${q?.limit_renders || 'a few'} room pictures per 24 hours. You can keep chatting — and our team can prepare a picture for you.`;
    case 'conversation':
      return 'Long conversations cost more to read. Start a new one to continue — just repeat the essentials.';
    case 'blocked':
      return 'The assistant has been switched off for this device or account. Our team can help you directly.';
    case 'burst':
      return 'You are sending messages very quickly. Give it a minute and try again.';
    default:
      return props.limit.message;
  }
});
const resetClock = computed(() => {
  if (!props.limit.resetsAt) return '';
  const d = new Date(props.limit.resetsAt);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + (sameDay ? '' : ` ${d.toLocaleDateString([], { weekday: 'short' })}`);
});
const showSignIn = computed(() => !props.signedIn && ['messages', 'photos'].includes(props.limit.reason));
const whatsappHref = whatsappLink('Hello Sweet Home Gallery, I reached the assistant limit on the website and would like some help.');
</script>

<style>
.aic-limit { display: flex; gap: 14px; padding: 16px; border-radius: 16px; background: #fff; border: 1px solid #e8d9c4; box-shadow: 0 6px 18px rgba(23, 36, 48, 0.06); max-width: 560px; }
.aic-limit.is-blocked, .aic-limit.is-daily_budget, .aic-limit.is-monthly_budget { border-color: #f0b4ae; }
.aic-limit__icon { flex: 0 0 40px; height: 40px; border-radius: 12px; background: rgba(187, 151, 109, 0.14); color: #bb976d; display: flex; align-items: center; justify-content: center; }
.is-blocked .aic-limit__icon { background: rgba(180, 35, 24, 0.1); color: #b42318; }
.aic-limit__body { min-width: 0; flex: 1; }
.aic-limit__title { display: block; font-size: 15px; font-weight: 600; color: #172430; }
.aic-limit__text { margin: 4px 0 0; font-size: 13.5px; line-height: 1.5; color: #3c474e; }
.aic-limit__timer { margin: 8px 0 0; font-size: 13.5px; color: #172430; }
.aic-limit__timer b { font-variant-numeric: tabular-nums; color: #bb976d; }
.aic-limit__more { margin: 10px 0 0; font-size: 12.5px; color: #8a9197; }
.aic-limit__actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.aic-limit__btn { display: inline-flex; align-items: center; gap: 7px; height: 38px; padding: 0 14px; border-radius: 999px; font-size: 13px; font-weight: 600; text-decoration: none; border: 1px solid transparent; cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
.aic-limit__btn--dark { background: #172430; color: #fff !important; }
.aic-limit__btn--dark:hover { background: #bb976d; }
.aic-limit__btn--wa { background: #25d366; color: #fff !important; }
.aic-limit__btn--wa:hover { background: #1ebe5b; }
.aic-limit__btn--ghost { background: transparent; color: #172430; border-color: #dfe2e4; }
.aic-limit__btn--ghost:hover { border-color: #bb976d; color: #bb976d; }

.dark .aic-limit { background: #1e2a35; border-color: #3c474e; }
.dark .aic-limit__title, .dark .aic-limit__timer, .dark .aic-limit__btn--ghost { color: #fff; }
.dark .aic-limit__text { color: #cfd4d8; }
.dark .aic-limit__btn--ghost { border-color: #3c474e; }
</style>
